// src/pages/mc/DemandRichCard.tsx
//
// Correção UX v2 (PRINCÍPIO ZERO) — card de demanda LITERAL do wireframe
// RAIOS-UX-SIDEBAR-SPEC.html aba "05 Demandas" (.drow):
//   - dtop: copybtn + código (DEM-xxxx) + • + título;
//   - stp: chip do pipeline (.plchip) e, em execução, nome do step (.sname) +
//     badge "Etapa X/Y" (.stepb);
//   - dmeta: "Criada dd/mm hh:mm" + marco do status + elapsed (⏱) quando
//     em execução / aguardando aprovação.
//   - variante .drow.appr (aguardando aprovação supervised): apinfo clicável
//     + apacts com iconbtns Ver artefato / Aprovar / Reprovar (C2) — fluxo
//     canônico PATCH /v1/demands/:id/transition com modais de confirmação do
//     wireframe (dlgApprove / dlgReject com feedback opcional + reenvio).
//
// Anatomia CSS: wf-row* (wireframe.css) + wf-stp/wf-plchip/wf-stepb/wf-dmeta/
// wf-copybtn/wf-row--appr/wf-apacts (wf-demands.css, porte verbatim).
//
// Consumidores além do kanban (props públicas {demand, onClick, projects?}
// preservadas): BusinessDemandsTab (negócios) — `step` é opcional e só o
// kanban fornece.

import { useEffect, useState } from 'react';
import { Bot, Briefcase, Check, Clock, Copy, User, Workflow, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { api, ApiError, type Demand, type ProjectSummary } from '@/lib/api';
import { listPipelineRuns } from '@/lib/api-pipelines';
import { useDemandsStore } from '@/stores/demands-store';
import { fmtDayTime, fmtDuration } from '@/lib/format';
import { columnIdForStatus, demandAwaitsApproval, demandStatusLabel } from './demand-constants';
import { WaitingApprovalCard } from '@/components/demands/WaitingApprovalCard';
import { ElapsedClock } from '@/components/demands/ElapsedClock';

import '@/styles/wf-demands.css';

/* ── Helpers ───────────────────────────────────────────────────────────────── */

/**
 * Colunas terminais que exibem marco de status na dmeta
 * (wireframe: "Aprovada 08/06 16:20" / "Falhou …" / "Bloqueada …").
 * DEM-2614: o rótulo vem de demandStatusLabel(status) — NÃO do title da coluna —
 * para failed não mentir como "Bloqueada".
 */
const TERMINAL_META_COLUMNS = new Set([
  'aprovadas',
  'finalizadas',
  'reprovadas',
  'bloqueadas',
  'falharam',
  'canceladas',
]);

export interface DemandStepInfo {
  /** Índice 1-based do step CORRENTE da run (numerador). DEM-2051: null quando
   *  o servidor não derivou a etapa (step indisponível) → a UI mostra "—",
   *  NUNCA um número fabricado a partir de contagem de tasks. */
  current: number | null;
  total: number;
  name?: string | null;
  /** Slug canônico do step corrente (ex: development, auto-review, peer-review)
   *  — derivado server-side (current_step_slug). DEM-2001. */
  slug?: string | null;
  /** Ator (agente) que atua no step corrente, derivado do slug. DEM-2001:
   *  evita mostrar "Peer Review" quando Bezalel está em development/auto-review. */
  actor?: string | null;
  /** Nome LEGÍVEL do pipeline da run (pipelines.name) — segunda linha do card
   *  (DEM-2051). Quando ausente, a UI cai no slug da demanda. */
  pipelineName?: string | null;
  /** true quando current/total foram DERIVADOS do servidor (etapa real), false
   *  quando o servidor não derivou (estado neutro "—"). DEM-2001/DEM-2051. */
  fromServer?: boolean;
}

/* ── Copy button (wireframe .copybtn + estado .copied) ─────────────────────── */

function CopyCodeBtn({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copiar código"
      aria-label={`Copiar ${code}`}
      className={cn('wf-copybtn', copied && 'copied')}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      <Copy className="ic" aria-hidden="true" />
    </button>
  );
}

/* ── Modais aprovar / reprovar (wireframe dlgApprove / dlgReject) ──────────── */

// Montado APENAS enquanto aberto (estado inicial fresco a cada abertura —
// campo de contexto vazio por padrão).
function ApproveDemandDialog({
  code,
  busy,
  onClose,
  onConfirm,
}: {
  code: string;
  busy: boolean;
  onClose: () => void;
  // DEM-2038: contexto opcional do usuário propagado como `notes` na transição.
  onConfirm: (info: string) => void;
}) {
  const [info, setInfo] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm" className="max-w-[440px] w-[92vw] p-0 gap-0 overflow-hidden" hideClose>
        <DialogTitle className="sr-only">Aprovar demanda</DialogTitle>
        <DialogDescription className="sr-only">Confirmação de aprovação da demanda {code}</DialogDescription>
        <div className="wf-modal-header">
          <Check className="ic" style={{ color: 'var(--wf-success-text)' }} aria-hidden="true" />
          Aprovar demanda
        </div>
        <div className="wf-modal-body">
          Confirmar aprovação da demanda <span className="wf-row-code">{code}</span>? Ela será{' '}
          <strong>concluída e finalizada</strong>.
          <div className="wf-resend on">
            <label htmlFor={`approve-info-${code}`}>Contexto adicional (opcional)</label>
            <textarea
              id={`approve-info-${code}`}
              placeholder="Observações da aprovação…"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
            />
          </div>
        </div>
        <div className="wf-modal-footer">
          <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn-primary"
            onClick={() => onConfirm(info)}
            disabled={busy}
          >
            Aprovar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Montado APENAS enquanto aberto (estado inicial fresco a cada abertura —
// checkbox OFF por padrão, como no wireframe).
function RejectDemandDialog({
  demand,
  busy,
  onClose,
  onConfirm,
}: {
  demand: Demand;
  busy: boolean;
  onClose: () => void;
  onConfirm: (resend: boolean, info: string, returnStep: string) => void;
}) {
  const code = demand.code ?? `#${demand.id.slice(0, 8)}`;
  const [resend, setResend] = useState(false);
  const [info, setInfo] = useState('');
  const [returnStep, setReturnStep] = useState('');
  const [stepOptions, setStepOptions] = useState<Array<{ value: string; label: string }>>([]);

  // Carrega as etapas do pipeline run da demanda (lazy, só quando o modal abre).
  useEffect(() => {
    let cancelled = false;
    listPipelineRuns({ demand_id: demand.id })
      .then((runs) => {
        if (cancelled) return;
        const latest = [...runs].sort(
          (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
        )[0];
        const steps =
          (latest?.config?.['steps'] as Array<{ id?: string; name?: string }> | undefined) ?? [];
        const opts = steps.map((s, i) => ({
          value: s.id ?? String(i + 1),
          label: `${i + 1} · ${s.name ?? s.id ?? `Etapa ${i + 1}`}`,
        }));
        setStepOptions(opts);
        setReturnStep(opts[0]?.value ?? '');
      })
      .catch(() => setStepOptions([]));
    return () => {
      cancelled = true;
    };
  }, [demand.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm" className="max-w-[440px] w-[92vw] p-0 gap-0 overflow-hidden" hideClose>
        <DialogTitle className="sr-only">Reprovar demanda</DialogTitle>
        <DialogDescription className="sr-only">Confirmação de reprovação da demanda {code}</DialogDescription>
        <div className="wf-modal-header">
          <X className="ic" style={{ color: 'var(--wf-danger-text)' }} aria-hidden="true" />
          Reprovar demanda
        </div>
        <div className="wf-modal-body">
          Confirmar reprovação da demanda <span className="wf-row-code">{code}</span>?
          <div className="wf-chkrow">
            <input
              id={`chk-resend-${demand.id}`}
              type="checkbox"
              checked={resend}
              onChange={(e) => setResend(e.target.checked)}
            />
            <label htmlFor={`chk-resend-${demand.id}`}>
              Reenviar ao pipeline
              <small>Marque pra devolver o trabalho a um worker, com instruções e a etapa de retorno.</small>
            </label>
          </div>
          <div className={cn('wf-resend', resend && 'on')}>
            <label htmlFor={`resend-info-${demand.id}`}>Informações adicionais</label>
            <textarea
              id={`resend-info-${demand.id}`}
              placeholder="O que precisa ser refeito / corrigido…"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
            />
            <label htmlFor={`resend-step-${demand.id}`}>Etapa de retorno</label>
            <select
              id={`resend-step-${demand.id}`}
              value={returnStep}
              onChange={(e) => setReturnStep(e.target.value)}
            >
              {(stepOptions.length > 0
                ? stepOptions
                : [{ value: '', label: 'Início do pipeline' }]
              ).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="wf-wnote">
            Só demandas supervised passam por aqui. Unsupervised seguem o fluxo automático.
          </div>
        </div>
        <div className="wf-modal-footer">
          <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn-danger"
            disabled={busy}
            onClick={() => onConfirm(resend, info, returnStep)}
          >
            Reprovar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Component ─────────────────────────────────────────────────────────────── */

interface DemandRichCardProps {
  demand: Demand;
  onClick: (demand: Demand) => void;
  /** Lista de projetos da page — fallback do chip quando não há pipeline. */
  projects?: ProjectSummary[];
  /** Etapa atual do pipeline run (badge "Etapa X/Y" — só em execução). */
  step?: DemandStepInfo | null;
  /** Nome do negócio (business) da demanda — badge da linha 3 (DEM-2036). */
  businessName?: string | null;
  /**
   * DEM-2162 — badge do tenant DONO da demanda. Só é exibido quando fornecido
   * (contexto de business colaborado). Genérico: o rótulo vem do owner_tenant
   * resolvido pela API (nunca hardcoded). `isOwn` estiliza a demanda própria.
   */
  ownerLabel?: string | null;
  isOwnDemand?: boolean;
}

export function DemandRichCard({ demand, onClick, step, businessName, ownerLabel, isOwnDemand }: DemandRichCardProps) {
  const colId = columnIdForStatus(demand.status);
  const code = demand.code ?? `#${demand.id.slice(0, 8)}`;

  // Executor só aparece quando a API o resolveu no registry. Assim uma demanda
  // sem executor não ganha um rótulo falso de pipeline/squad.
  const executionExecutor = demand.execution_executor;
  const chipLabel = executionExecutor
    ? `${executionExecutor.type === 'squad' ? 'Squad' : 'Pipeline'} · ${executionExecutor.name}`
    : '—';

  // FONTE ÚNICA (demandAwaitsApproval): pending_approval/review = aguarda decisão humana,
  // INDEPENDENTE de supervision_mode. Uma unsupervised que escalou pra aprovação também
  // precisa dos botões (senão vira zumbi). Antes gateava em supervised → escondia o botão.
  const needsApproval = demandAwaitsApproval(demand);

  const transitionDemand = useDemandsStore((s) => s.transitionDemand);
  const fetchDemands = useDemandsStore((s) => s.fetchDemands);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleApproveConfirm = async (info: string) => {
    setBusy(true);
    try {
      // DEM-2038: contexto opcional vai como feedback { notes } na transição.
      // Backend ANTIGO (pré-restart) usa Zod estrito e rejeita o campo extra
      // com 4xx → degrada pra transição simples (mesmo padrão da reprovação).
      const trimmed = info.trim();
      if (trimmed) {
        try {
          await api.transitionDemand(demand.id, 'approved', { notes: trimmed });
        } catch (err) {
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            console.warn(
              '[DemandRichCard] backend pré-restart não aceita feedback na transição — aprovação aplicada sem contexto.',
            );
            await api.transitionDemand(demand.id, 'approved');
          } else {
            throw err;
          }
        }
        void fetchDemands();
      } else {
        // Optimistic update via store (PATCH /v1/demands/:id/transition).
        transitionDemand(demand.id, 'approved');
      }
      setApproveOpen(false);
    } catch (err) {
      console.error('[DemandRichCard] aprovar falhou:', err);
    } finally {
      setBusy(false);
    }
  };

  const handleRejectConfirm = async (resend: boolean, info: string, returnStep: string) => {
    setBusy(true);
    try {
      if (resend) {
        // Contrato novo: feedback { notes, return_step, resubmit } persiste em
        // metadata.rejection_feedback. Backend ANTIGO (pré-restart) rejeita o
        // campo extra com 4xx → degrada pra transição simples.
        const feedback = {
          ...(info.trim() ? { notes: info } : {}),
          ...(returnStep ? { return_step: returnStep } : {}),
          resubmit: true,
        };
        try {
          await api.transitionDemand(demand.id, 'rejected', feedback);
        } catch (err) {
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            console.warn(
              '[DemandRichCard] backend pré-restart não aceita feedback na transição — reprovação aplicada sem reenvio.',
            );
            await api.transitionDemand(demand.id, 'rejected');
          } else {
            throw err;
          }
        }
        void fetchDemands();
      } else {
        transitionDemand(demand.id, 'rejected');
      }
      setRejectOpen(false);
    } catch (err) {
      console.error('[DemandRichCard] reprovar falhou:', err);
    } finally {
      setBusy(false);
    }
  };

  /* dmeta — "Criada ..." + marco do status + elapsed (wireframe) */
  const createdTxt = fmtDayTime(demand.created_at);
  // DEM-2614: rótulo por status real (failed→"Falhou", blocked→"Bloqueada"),
  // não pelo title da coluna (que mentia "Bloqueada" para failed).
  const metaLabel = TERMINAL_META_COLUMNS.has(colId)
    ? demandStatusLabel(demand.status)
    : undefined;
  const showElapsed = colId === 'em_execucao' || colId === 'aguardando_aprovacao';
  const elapsedSince = showElapsed
    ? (colId === 'em_execucao' ? (demand.started_at ?? demand.created_at) : demand.updated_at)
    : null;
  // AGT-17 (admin): status TERMINAL mostra o tempo TOTAL decorrido da demanda
  // (criação → marco final, estático) — antes finalizada/bloqueada/cancelada
  // não exibiam tempo nenhum.
  // MTR-04 (admin 2026-06-12): duração terminal mede EXECUÇÃO — baseline
  // started_at, não created_at (dias em avaliação/aprovação não contam).
  // Sem started_at (legada/nunca executou) → sem badge ⏱.
  const terminalDurationMs =
    metaLabel && demand.updated_at && demand.started_at
      ? Math.max(0, Date.parse(demand.updated_at) - Date.parse(demand.started_at))
      : null;

  // F4 2026-07-19 (pedido do dono): autoria da demanda — o AGENTE do companion
  // que assinou a criação (ícone robô) + o USUÁRIO dono (ícone pessoa). Admin
  // 2026-07-20: fica na MESMA linha do chip de negócio (não em linha própria).
  // Dados denormalizados em metadata.creator_context no create; ausentes em
  // demandas antigas → some.
  const cc = (demand.metadata?.['creator_context'] ?? null) as Record<string, unknown> | null;
  const signerAgentName = typeof cc?.['signer_agent_name'] === 'string' ? (cc['signer_agent_name'] as string) : null;
  const ownerUserName = typeof cc?.['owner_user_name'] === 'string' ? (cc['owner_user_name'] as string) : null;
  const authorship = (signerAgentName || ownerUserName) ? (
    <span className="wf-authorship" data-testid="demand-authorship">
      {signerAgentName && (
        <span title="Companion (agente) que criou a demanda">
          <Bot className="ic" aria-hidden="true" />
          {signerAgentName}
        </span>
      )}
      {ownerUserName && (
        <span title="Usuário dono da demanda">
          <User className="ic" aria-hidden="true" />
          {ownerUserName}
        </span>
      )}
    </span>
  ) : null;

  const dmeta = (
    <div className="wf-dmeta">
      <span>
        <Clock className="ic" aria-hidden="true" />
        Criada {createdTxt}
      </span>
      {colId === 'em_execucao' && demand.started_at && <span>Início {fmtDayTime(demand.started_at)}</span>}
      {colId === 'aguardando_aprovacao' && <span>Em aprov. {fmtDayTime(demand.updated_at)}</span>}
      {metaLabel && <span>{metaLabel} {fmtDayTime(demand.updated_at)}</span>}
      {elapsedSince && <ElapsedClock since={elapsedSince} className="elapsed" />}
      {terminalDurationMs != null && terminalDurationMs > 0 && (
        <span className="elapsed" title="Tempo total da demanda (criação até o marco final)">
          ⏱ {fmtDuration(terminalDurationMs)}
        </span>
      )}
    </div>
  );

  /* ── Variante aguardando aprovação (wireframe .drow.appr) ────────────────── */
  // Card compartilhado com a Dashboard (components/demands/WaitingApprovalCard)
  // — unificação 2026-06-11. Os diálogos de aprovar/reprovar seguem locais.
  if (needsApproval) {
    return (
      <WaitingApprovalCard
        demand={demand}
        testId={`demand-card-${demand.id}`}
        meta={dmeta}
        onOpen={onClick}
        onApprove={() => setApproveOpen(true)}
        onReject={() => setRejectOpen(true)}
      >
        {approveOpen && (
          <ApproveDemandDialog
            code={code}
            busy={busy}
            onClose={() => setApproveOpen(false)}
            onConfirm={(info) => void handleApproveConfirm(info)}
          />
        )}
        {rejectOpen && (
          <RejectDemandDialog
            demand={demand}
            busy={busy}
            onClose={() => setRejectOpen(false)}
            onConfirm={(r, i, s) => void handleRejectConfirm(r, i, s)}
          />
        )}
      </WaitingApprovalCard>
    );
  }

  /* ── Card padrão (wireframe .drow) ───────────────────────────────────────── */
  return (
    <div
      className="wf-row"
      role="button"
      tabIndex={0}
      onClick={() => onClick(demand)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(demand)}
      data-testid={`demand-card-${demand.id}`}
    >
      <div className="wf-row-top">
        <CopyCodeBtn code={code} />
        <span className="wf-row-code">{code}</span>
        <span className="wf-row-sep">•</span>
        <span className="wf-row-title">{demand.title}</span>
        {/* DEM-2162 — badge do tenant DONO (só em business colaborado). Genérico:
            rótulo dinâmico do owner_tenant; realça a demanda própria vs. do colab. */}
        {ownerLabel && (
          <span
            className="wf-owner-badge"
            data-testid="demand-owner-badge"
            data-own={isOwnDemand ? 'true' : 'false'}
            title={`Dono: ${ownerLabel}`}
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '.66rem',
              fontWeight: 600,
              padding: '2px 7px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
              color: isOwnDemand ? 'var(--wf-accent-text, #2563eb)' : 'var(--wf-muted)',
              background: isOwnDemand
                ? 'var(--wf-accent-bg, rgba(37,99,235,.10))'
                : 'var(--wf-chip-bg, rgba(120,120,120,.10))',
            }}
          >
            <User style={{ width: 11, height: 11 }} aria-hidden="true" />
            {ownerLabel}
          </span>
        )}
      </div>
      <div className="wf-stp">
        <span className="wf-plchip">
          <Workflow className="ic" aria-hidden="true" />
          {chipLabel}
        </span>
        {/* DEM-2051: nome da etapa SEMPRE visível em run viva (corrige o sumiço
            quando step.name vinha null do antigo fallback por tasks). Em estado
            neutro (current null), o nome fica omitido e o badge mostra "—". */}
        {step?.name && <span className="sname">{step.name}</span>}
        {/* DEM-2036: nome do agente (.wf-actor / demand-step-actor) REMOVIDO
            do card — o ator segue DERIVADO em demand-step-info, só não é exibido. */}
        {step && (
          <span className="wf-stepb" data-testid="demand-step-badge">
            {step.current != null ? `Etapa ${step.current}/${step.total}` : 'Etapa —'}
          </span>
        )}
      </div>
      {/* DEM-2036 — linha do negócio (business chip). Admin 2026-07-20: a
          autoria (companion + usuário) fica NA MESMA LINHA, não em linha própria.
          A linha aparece se houver negócio OU autoria. */}
      {(businessName || authorship) && (
        <div className="wf-bizline">
          {businessName && (
            <span className="wf-bizchip" data-testid="demand-business">
              <Briefcase className="ic" aria-hidden="true" />
              {businessName}
            </span>
          )}
          {authorship}
        </div>
      )}
      {dmeta}
    </div>
  );
}
